import { storage as localStorage } from './storage';
import { db, storage, isFirebaseConfigured } from './firebaseConfig';
import { collection, addDoc, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export interface AIMessage {
    role: 'user' | 'assistant';
    content: string;
    timestamp: any;
}

export interface AISession {
    id: string;
    userId: string;
    startTime: any;
    messages: AIMessage[];
}

const KEYS = {
    AI_HISTORY: 'sbaj_ai_history'
};

export const aiService = {
    saveSession: async (userId: string, messages: AIMessage[]): Promise<string> => {
        if (isFirebaseConfigured() && db) {
            try {
                const docRef = await addDoc(collection(db, 'ai_history'), {
                    userId,
                    startTime: Timestamp.now(),
                    messages
                });
                return docRef.id;
            } catch (error) {
                console.error('Error saving AI session to Firebase:', error);
                throw error;
            }
        }

        // Mock Fallback
        const sessions = localStorage.get<AISession[]>(KEYS.AI_HISTORY, []);
        const newSession: AISession = {
            id: Date.now().toString(),
            userId,
            startTime: new Date().toISOString(),
            messages
        };
        localStorage.set(KEYS.AI_HISTORY, [...sessions, newSession]);
        return newSession.id;
    },

    getUserHistory: async (userId: string): Promise<AISession[]> => {
        if (isFirebaseConfigured() && db) {
            try {
                const q = query(collection(db, 'ai_history'), where('userId', '==', userId), orderBy('startTime', 'desc'));
                const snapshot = await getDocs(q);
                return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as AISession));
            } catch (error) {
                console.error('Error fetching AI history from Firebase:', error);
                return [];
            }
        }

        // Mock Fallback
        const sessions = localStorage.get<AISession[]>(KEYS.AI_HISTORY, []);
        return sessions.filter(s => s.userId === userId).sort((a, b) =>
            new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
        );
    },

    generateContent: async (prompt: string, type: 'page' | 'blog' | 'hero' | 'training_video', config?: any): Promise<any> => {
        console.log('[AI Service] generateContent called with:', { prompt, type, provider: config?.textGeneration?.provider });

        // Check if Text Generation is enabled (or Video for training_video type + Gemini)
        let provider = config?.textGeneration?.provider;
        let apiKey = config?.textGeneration?.keys?.[provider];
        let model = config?.textGeneration?.model;
        // let generationConfig = config?.textGeneration; // Unused

        // Special handling for training_video if Video Config is set to Google (Gemini)
        if (type === 'training_video' && config?.videoGeneration?.enabled && config?.videoGeneration?.provider === 'google') {
            console.log('[AI Service] Using Google Gemini from Video Configuration for training content...');
            provider = 'google';
            apiKey = config?.videoGeneration?.keys?.google;
            model = config?.videoGeneration?.model;
            // generationConfig = config?.videoGeneration; // Unused
        }

        if ((config?.textGeneration?.enabled || (type === 'training_video' && config?.videoGeneration?.enabled)) && apiKey) {
            try {
                let result;
                if (provider === 'openai') {
                    console.log('[AI Service] Using OpenAI...');
                    result = await generateWithOpenAI(prompt, type, apiKey, model, config);
                } else if (provider === 'google') {
                    console.log('[AI Service] Using Google Gemini...');
                    result = await generateWithGoogle(prompt, type, apiKey, model, config);
                } else if (provider === 'anthropic') {
                    console.log('[AI Service] Using Anthropic Claude...');
                    result = await generateWithAnthropic(prompt, type, apiKey, model, config);
                }

                if (result) {
                    console.log('[AI Service] Generation successful:', result);
                    return result;
                }
            } catch (error) {
                console.error(`[AI Service] ${provider} generation failed entirely:`, error);
                // CRITICAL CHANGE: Do NOT fallback to mock if the user provided an API key. 
                // We want the user to see the actual error (e.g. Quota Exceeded, Invalid Key).
                throw error;
            }
        } else {
            console.warn('[AI Service] Text generation disabled or missing API key. Config:', config?.textGeneration);
            // If no API key is provided, we can fallback or throw. 
            // Let's throw to prompt them to configure it.
            throw new Error('AI Text Generation is disabled or missing an API Key. Please check AI Integration settings.');
        }

        // Unreachable code if we throw above, but keeping just in case logic changes
        await localStorage.delay(2000);

        if (type === 'page') {
            return {
                title: prompt,
                slug: `/${prompt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
                content: `<h1>${prompt}</h1>
                <p class="lead">Comprehensive guide and resources about ${prompt}.</p>
                <h2>Introduction</h2>
                <p>Welcome to our dedicated page for <strong>${prompt}</strong>. At SBAJ, we believe in providing top-tier resources to help Jamaican SMEs navigate the digital landscape securely.</p>
                <h2>Key Information</h2>
                <ul>
                    <li>Understanding the basics of ${prompt}</li>
                    <li>Implementation strategies for small businesses</li>
                    <li>Risk mitigation and compliance</li>
                </ul>
                <p>Contact our team for a personalized consultation regarding ${prompt}.</p>`
            };
        } else if (type === 'blog') {
            return {
                title: `Understanding ${prompt}: A Comprehensive Guide`,
                excerpt: `Everything you need to know about ${prompt} and how it impacts your business security.`,
                content: `In today's digital landscape, ${prompt} has emerged as a critical concern for businesses of all sizes. \n\n1. What is ${prompt}?\nIt refers to... \n\n2. Why it matters\nIgnoring this can lead to... \n\n3. Best Practices\n- Stay updated\n- Train your team\n- Use proper tools`,
                category: 'Security',
                author: 'SBAJ Team',
                imageUrl: `https://source.unsplash.com/800x600/?${encodeURIComponent(prompt)}&sig=${Date.now()}`
            };
        } else if (type === 'hero') {
            return {
                title: `AI-Powered Security Solution`,
                subtitle: `Leverage cutting-edge artificial intelligence to predict and prevent cyber threats related to ${prompt} before they happen.`,
                ctaText: "Start AI Analysis"
            };
        } else if (type === 'training_video') {
            return {
                script: `Welcome to this training session on ${prompt}. Today we will cover the essential aspects of securing your digital environment.`,
                slides: [
                    { title: `Introduction to ${prompt}`, points: ['Overview', 'Importance', 'Key Concepts'] },
                    { title: 'Best Practices', points: ['Use strong passwords', 'Enable MFA', 'Regular Updates'] },
                    { title: 'Summary', points: ['Stay vigilant', 'Report incidents', 'Continuous learning'] }
                ],
                duration: '5 min'
            };
        }

        return null;
    },

    generateChatResponse: async (message: string, config: any): Promise<string> => {
        const provider = config?.textGeneration?.provider;
        const apiKey = config?.textGeneration?.keys?.[provider];

        if (config?.textGeneration?.enabled && apiKey) {
            try {
                if (provider === 'openai') {
                    return await chatWithOpenAI(message, apiKey, config.textGeneration.model);
                } else if (provider === 'google') {
                    return await chatWithGoogle(message, apiKey, config.textGeneration.model);
                } else if (provider === 'anthropic') {
                    return await chatWithAnthropic(message, apiKey, config.textGeneration.model);
                }
            } catch (error) {
                console.error('Chat generation failed:', error);
                throw error;
            }
        }

        throw new Error('AI not configured');
    },


    generateAudio: async (text: string, config: any): Promise<string | null> => {
        // const provider = 'openai'; // Unused


        // Resolve API Key: Priority -> Audio Config -> Text Config -> Video Config
        let apiKey = config?.audioGeneration?.keys?.openai ||
            config?.textGeneration?.keys?.openai ||
            config?.videoGeneration?.keys?.openai;

        if (!apiKey) {
            console.warn('No OpenAI API key found for audio generation');
            return null;
        }

        const voice = config?.audioGeneration?.voice || 'alloy';

        try {
            console.log(`[AI Service] Generating audio with OpenAI TTS (Voice: ${voice})...`);
            console.log(`[AI Service] Using API Key: ${apiKey ? apiKey.substring(0, 7) + '...' : 'NONE'}`);

            const response = await fetch('https://api.openai.com/v1/audio/speech', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiKey}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    model: 'tts-1',
                    input: text,
                    voice: voice // Options: alloy, echo, fable, onyx, nova, shimmer
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                let cleanError = errorText;
                try {
                    cleanError = JSON.parse(errorText)?.error?.message || errorText;
                } catch (e) { }
                console.error('OpenAI TTS Error:', cleanError);
                throw new Error(`OpenAI TTS Failed: ${cleanError}`);
            }

            const blob = await response.blob();

            // Upload to Firebase Storage
            if (isFirebaseConfigured() && storage) {
                const filename = `training/audio/${Date.now()}_${Math.random().toString(36).substring(7)}.mp3`;
                const storageRef = ref(storage, filename);
                await uploadBytes(storageRef, blob);
                const url = await getDownloadURL(storageRef);
                return url;
            } else {
                console.warn('[AI Service] Firebase Storage not configured. Converting audio to Base64 for local persistence.');

                // Convert Blob to Base64 Data URL for local storage persistence
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            }

        } catch (error) {
            console.error('Audio generation failed:', error);
            throw error; // Re-throw to allow caller to handle/report
        }
    }
};

// Helper to generate image based on configuration
async function generateImage(prompt: string, config: any, context?: string): Promise<string> {
    try {
        const provider = config?.imageGeneration?.provider;
        const apiKey = config?.imageGeneration?.keys?.[provider];

        if (config?.imageGeneration?.enabled) {
            console.log(`[AI Service] Generating image with ${provider}...`);

            if (provider === 'dalle' && apiKey) {
                const response = await fetch('https://api.openai.com/v1/images/generations', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${apiKey}`
                    },
                    body: JSON.stringify({
                        model: config.imageGeneration.model || 'dall-e-3',
                        prompt: `A professional, high-quality feature image for a blog post about: ${prompt}. Minimalist, technology, cybersecurity theme. ${context ? `Category/Topic Context: ${context}` : ''}`,
                        n: 1,
                        size: '1024x1024'
                    })
                });

                if (response.ok) {
                    const data = await response.json();
                    return data.data[0].url;
                } else {
                    console.error('DALL-E Error:', await response.text());
                }
            }
            // Add other providers here (Stability, etc.)
        }
    } catch (e) {
        console.error('Image generation failed:', e);
    }

    // Reliable Fallback (Picsum or similar, source.unsplash is deprecated)
    // Using a reliable placeholder service with a deterministic seed based on prompt
    const seed = prompt.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return `https://picsum.photos/seed/${seed}/800/600`;
}

// Helper function for OpenAI
async function generateWithOpenAI(prompt: string, type: 'page' | 'blog' | 'hero' | 'training_video', apiKey: string, model: string, config?: any) {
    const actualModel = resolveModel('openai', model);
    let systemPrompt = '';
    let userMessage = '';

    // Build constraints string
    let constraints = '';
    if (config?.guardrails) {
        if (config.guardrails.tone) {
            constraints += ` Tone: ${config.guardrails.tone}.`;
        }
        if (config.guardrails.forbiddenWords && config.guardrails.forbiddenWords.length > 0) {
            constraints += ` Do NOT use these words: ${config.guardrails.forbiddenWords.join(', ')}.`;
        }
    }

    if (type === 'page') {
        systemPrompt = `You are an expert web content writer for a Cybersecurity portal (SBAJ). Return JSON with fields: title, slug (url friendly), content (HTML format with headings, paragraphs, lists).${constraints}`;
        if (config?.templates?.page) {
            systemPrompt += `\n\nIMPORTANT STRUCTURE INSTRUCTIONS:\n${config.templates.page}`;
        }
        userMessage = `Write a comprehensive page about: ${prompt}`;
    } else if (type === 'blog') {
        // Persona Definitions
        const personas: Record<string, string> = {
            expert: "Focus on technical accuracy, architecture, implementation details, and deep security concepts. Audience: IT Professionals/CTOs.",
            advisor: "Focus on business value, ROI, risk management, and strategic decision making. Audience: Executives/Business Owners.",
            analyst: "Focus on market trends, statistics, comparative analysis, and future outlook. Audience: Researchers/Analysts.",
            guide: "Focus on actionable steps, simplicity, practical advice, and real-world examples. Audience: Small Business Owners."
        };
        const selectedPersona = config?.persona || 'expert';
        const personaInstruction = personas[selectedPersona];

        systemPrompt = `You are an expert cybersecurity blogger with an academic writing style. 
        PERSONA: ${personaInstruction}
        Return JSON with fields: title, excerpt, content, category, author.
        
        CRITICAL CONTENT INSTRUCTIONS:
        1. Format as valid HTML5 using <h2>, <p>, <ul>, <li>, <strong> tags.
        2. Start with a clear <h2>Introduction</h2> section.
        3. Use <strong>bold</strong> for key terms and emphasis where appropriate.
        4. Maintain professional, academic tone with proper spacing between sections.
        
        STYLE CONSTRAINTS:
        - DO NOT use bullet points unless absolutely necessary (max 1 list per post).
        - DO NOT use em dashes (—). Use parentheses or commas instead.
        ${constraints}`;

        if (config?.templates?.blog) {
            systemPrompt += `\n\nIMPORTANT STRUCTURE INSTRUCTIONS:\n${config.templates.page}`;
        }
        userMessage = `Write a comprehensive, well-structured blog post about: ${prompt}. Do NOT mention it is AI generated. Use 'SBAJ Team' as author if not specified.`;
    } else if (type === 'hero') {
        systemPrompt = `You are a marketing copywriter. Return JSON with fields: title, subtitle, ctaText.${constraints}`;
        userMessage = `Write hero section copy for: ${prompt}`;
    } else if (type === 'training_video') {
        systemPrompt = `You are an expert instructional designer for cybersecurity training. Return JSON with fields: 
        - script (the spoken narration script, engaging and clear)
        - slides (array of objects with 'title' and 'points' array). IMPORTANT: Break content into sections. Start new sections with a dedicated slide having the Section Title and an empty points array (or just 1 point like "Section Overview").
        - duration (estimated duration string)
        - quiz (array of 3-5 questions: {question, options: string[], correctAnswer: number (0-index)})
        ${constraints}`;
        userMessage = `Create a training video script and slide deck about: ${prompt}`;
    }

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
            model: actualModel || 'gpt-3.5-turbo',
            messages: [
                { role: 'system', content: systemPrompt },
                { role: 'user', content: userMessage }
            ],
            temperature: 0.7
        })
    });

    if (!response.ok) {
        throw new Error(`OpenAI API Error: ${response.statusText}`);
    }

    const data = await response.json();
    const content = data.choices[0].message.content;

    try {
        // Attempt to parse JSON from the response
        // Sometimes LLMs add markdown code blocks around JSON
        const jsonStr = content.replace(/^```json\n|\n```$/g, '').trim();
        const parsed = JSON.parse(jsonStr);

        // If it's a blog post, inject an image URL if one wasn't provided by the LLM (which is likely)
        if (type === 'blog' && !parsed.imageUrl) {
            parsed.imageUrl = await generateImage(prompt, config, parsed.category);
        }

        return parsed;
    } catch (e) {
        console.error('Failed to parse AI response as JSON:', content);
        throw new Error('Invalid JSON response from AI');
    }
}

// Helper to map future/fictional model IDs to real working ones
function resolveModel(provider: string, modelId: string): string {
    const mappings: Record<string, Record<string, string>> = {
        openai: {
            'gpt-5.1-turbo': 'gpt-4-turbo',
            'gpt-5': 'gpt-4-turbo',
            'gpt-4o': 'gpt-4-turbo-preview' // or gpt-4o if available in your tier
        },
        anthropic: {
            'claude-4.5-opus': 'claude-3-opus-20240229',
            'claude-4.5-sonnet': 'claude-3-sonnet-20240229'
        },
        google: {
            'gemini-3.0-pro': 'gemini-2.0-flash-exp'
        }
    };

    return mappings[provider]?.[modelId] || modelId;
}

// Helper function for Google Gemini
async function generateWithGoogle(prompt: string, type: 'page' | 'blog' | 'hero' | 'training_video', apiKey: string, model: string, config?: any) {
    const actualModel = resolveModel('google', model);
    let systemPrompt = '';
    let userMessage = '';

    // Build constraints string for system prompt
    let constraints = '';
    if (config?.guardrails) {
        if (config.guardrails.tone) {
            constraints += ` Tone: ${config.guardrails.tone}.`;
        }
        if (config.guardrails.forbiddenWords && config.guardrails.forbiddenWords.length > 0) {
            constraints += ` Do NOT use these words: ${config.guardrails.forbiddenWords.join(', ')}.`;
        }
    }

    if (type === 'page') {
        systemPrompt = `You are an expert web content writer for a Cybersecurity portal (SBAJ). Return ONLY a valid JSON object with fields: title, slug (url friendly), content (HTML format with headings, paragraphs, lists).${constraints}`;
        if (config?.templates?.page) {
            systemPrompt += `\n\nIMPORTANT STRUCTURE INSTRUCTIONS:\n${config.templates.page}`;
        }
        userMessage = `Write a comprehensive page about: ${prompt}`;
    } else if (type === 'blog') {
        // Persona Definitions
        const personas: Record<string, string> = {
            expert: "Focus on technical accuracy, architecture, implementation details, and deep security concepts. Audience: IT Professionals/CTOs.",
            advisor: "Focus on business value, ROI, risk management, and strategic decision making. Audience: Executives/Business Owners.",
            analyst: "Focus on market trends, statistics, comparative analysis, and future outlook. Audience: Researchers/Analysts.",
            guide: "Focus on actionable steps, simplicity, practical advice, and real-world examples. Audience: Small Business Owners."
        };
        const selectedPersona = config?.persona || 'expert';
        const personaInstruction = personas[selectedPersona];

        systemPrompt = `You are an expert cybersecurity blogger with an academic writing style. 
        PERSONA: ${personaInstruction}
        Return ONLY a valid JSON object with fields: title, excerpt, content, category, author.
        
        CRITICAL CONTENT INSTRUCTIONS:
        1. Format as valid HTML5 using <h2>, <p>, <ul>, <li>, <strong> tags.
        2. Start with a clear <h2>Introduction</h2> section.
        3. Use <strong>bold</strong> for key terms and emphasis where appropriate.
        4. Maintain professional, academic tone with proper spacing between sections.

        STYLE CONSTRAINTS:
        - DO NOT use bullet points unless absolutely necessary (max 1 list per post).
        - DO NOT use em dashes (—). Use parentheses or commas instead.
        ${constraints}`;

        if (config?.templates?.blog) {
            systemPrompt += `\n\nIMPORTANT STRUCTURE INSTRUCTIONS:\n${config.templates.blog}`;
        }
        userMessage = `Write a comprehensive, well-structured blog post about: ${prompt}. Do NOT mention it is AI generated. Use 'SBAJ Team' as author if not specified.`;
    } else if (type === 'hero') {
        systemPrompt = `You are a marketing copywriter. Return ONLY a valid JSON object with fields: title, subtitle, ctaText.${constraints}`;
        userMessage = `Write hero section copy for: ${prompt}`;
    } else if (type === 'training_video') {
        systemPrompt = `You are an expert instructional designer. Return ONLY a valid JSON object with fields: 
        - script (spoken narration)
        - slides (array of {title, points[]}). Use Section Header slides (title + empty points) to introduce topics.
        - quiz (array of 3-5 questions: {question, options: string[], correctAnswer: number (0-index)})
        - duration
        ${constraints}`;
        userMessage = `Create a training video script, slides, and assessment quiz about: ${prompt}`;
    }

    // Gemini doesn't have a separate system role in the same way for the v1beta API text-only models (gemini-pro often takes it as part of context or system instruction if available). 
    // For simplicity and compatibility with 'gemini-pro', we'll prepend the system prompt.
    const fullPrompt = `${systemPrompt}\n\nUser Request: ${userMessage}`;
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${actualModel || 'gemini-1.5-pro'}:generateContent?key=${apiKey}`;

    console.log('[AI Service] Google Request URL:', url);

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            contents: [{
                parts: [{ text: fullPrompt }]
            }],
            generationConfig: {
                temperature: 0.7
            }
        })
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('[AI Service] Google API Error Body:', errorText);

        // Auto-diagnose 404 errors by listing available models
        if (response.status === 404) {
            try {
                console.log('[AI Service] Attempting to list available models...');
                const listUrl = `https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`;
                const listResponse = await fetch(listUrl);
                if (listResponse.ok) {
                    const listData = await listResponse.json();
                    const availableModels = listData.models
                        ?.filter((m: any) => m.supportedGenerationMethods?.includes('generateContent'))
                        .map((m: any) => m.name.replace('models/', ''))
                        .join(', ');

                    if (availableModels) {
                        throw new Error(`Model '${actualModel}' not found. Available models for your key: ${availableModels}`);
                    }
                }
            } catch (listErr) {
                console.error('[AI Service] Failed to list models:', listErr);
            }
        }

        throw new Error(`Google Gemini API Error: ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    // Path to text: candidates[0].content.parts[0].text
    const content = data.candidates?.[0]?.content?.parts?.[0]?.text;

    if (!content) {
        throw new Error('No content returned from Gemini API');
    }

    try {
        const jsonStr = content.replace(/^```json\n|\n```$/g, '').replace(/^```\n|\n```$/g, '').trim();
        const parsed = JSON.parse(jsonStr);

        if (type === 'blog' && !parsed.imageUrl) {
            parsed.imageUrl = await generateImage(prompt, config, parsed.category);
        }

        return parsed;
    } catch (e) {
        console.error('Failed to parse Gemini response as JSON:', content);
        throw new Error('Invalid JSON response from AI');
    }
}

// Helper function for Anthropic Claude
async function generateWithAnthropic(prompt: string, type: 'page' | 'blog' | 'hero' | 'training_video', apiKey: string, model: string, config?: any) {
    const actualModel = resolveModel('anthropic', model);
    let systemPrompt = '';
    let userMessage = '';

    let constraints = '';
    if (config?.guardrails) {
        if (config.guardrails.tone) {
            constraints += ` Tone: ${config.guardrails.tone}.`;
        }
        if (config.guardrails.forbiddenWords && config.guardrails.forbiddenWords.length > 0) {
            constraints += ` Do NOT use these words: ${config.guardrails.forbiddenWords.join(', ')}.`;
        }
    }

    if (type === 'page') {
        systemPrompt = `You are an expert web content writer for a Cybersecurity portal (SBAJ). Return ONLY a valid JSON object with fields: title, slug (url friendly), content (HTML format with headings, paragraphs, lists).${constraints}`;
        if (config?.templates?.page) {
            systemPrompt += `\n\nIMPORTANT STRUCTURE INSTRUCTIONS:\n${config.templates.page}`;
        }
        userMessage = `Write a comprehensive page about: ${prompt}`;
    } else if (type === 'blog') {
        // Persona Definitions
        const personas: Record<string, string> = {
            expert: "Focus on technical accuracy, architecture, implementation details, and deep security concepts. Audience: IT Professionals/CTOs.",
            advisor: "Focus on business value, ROI, risk management, and strategic decision making. Audience: Executives/Business Owners.",
            analyst: "Focus on market trends, statistics, comparative analysis, and future outlook. Audience: Researchers/Analysts.",
            guide: "Focus on actionable steps, simplicity, practical advice, and real-world examples. Audience: Small Business Owners."
        };
        const selectedPersona = config?.persona || 'expert';
        const personaInstruction = personas[selectedPersona];

        systemPrompt = `You are an expert cybersecurity blogger with an academic writing style. 
        PERSONA: ${personaInstruction}
        Return ONLY a valid JSON object with fields: title, excerpt, content, category, author.
        
        CRITICAL CONTENT INSTRUCTIONS:
        1. Format as valid HTML5 using <h2>, <p>, <ul>, <li>, <strong> tags.
        2. Start with a clear <h2>Introduction</h2> section.
        3. Use <strong>bold</strong> for key terms and emphasis where appropriate.
        4. Maintain professional, academic tone with proper spacing between sections.

        STYLE CONSTRAINTS:
        - DO NOT use bullet points unless absolutely necessary (max 1 list per post).
        - DO NOT use em dashes (—). Use parentheses or commas instead.
        ${constraints}`;

        if (config?.templates?.blog) {
            systemPrompt += `\n\nIMPORTANT STRUCTURE INSTRUCTIONS:\n${config.templates.blog}`;
        }
        userMessage = `Write a comprehensive, well-structured blog post about: ${prompt}. Do NOT mention it is AI generated. Use 'SBAJ Team' as author if not specified.`;
    } else if (type === 'hero') {
        systemPrompt = `You are a marketing copywriter. Return ONLY a valid JSON object with fields: title, subtitle, ctaText.${constraints}`;
        userMessage = `Write hero section copy for: ${prompt}`;
    } else if (type === 'training_video') {
        systemPrompt = `You are an expert instructional designer. Return ONLY a valid JSON object with fields: 
        - script (spoken narration)
        - slides (array of {title, points[]}). Use Section Header slides to structure content.
        - quiz (array of 3-5 questions: {question, options: string[], correctAnswer: number (0-index)})
        - duration
        ${constraints}`;
        userMessage = `Create a training video script and slides about: ${prompt}`;
    }

    const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey,
            'anthropic-version': '2023-06-01',
            'dangerously-allow-browser': 'true' // Attempt to allow browser usage if supported by their edge network
        },
        body: JSON.stringify({
            model: actualModel || 'claude-3-opus-20240229',
            max_tokens: 4000,
            system: systemPrompt,
            messages: [
                { role: 'user', content: userMessage }
            ]
        })
    });

    if (!response.ok) {
        const err = await response.text();
        console.error('Anthropic Error:', err);
        throw new Error(`Anthropic API Error: ${response.statusText}`);
    }

    const data = await response.json();
    const content = data.content?.[0]?.text;

    try {
        const jsonStr = content.replace(/^```json\n|\n```$/g, '').replace(/^```\n|\n```$/g, '').trim();
        const parsed = JSON.parse(jsonStr);

        if (type === 'blog' && !parsed.imageUrl) {
            parsed.imageUrl = await generateImage(prompt, config);
        }

        return parsed;
    } catch (e) {
        console.error('Failed to parse Anthropic response as JSON:', content);
        throw new Error('Invalid JSON response from AI');
    }
}

// --- Chat Helper Functions ---

async function chatWithOpenAI(message: string, apiKey: string, model: string): Promise<string> {
    const actualModel = resolveModel('openai', model);
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
            model: actualModel || 'gpt-3.5-turbo',
            messages: [
                { role: 'system', content: 'You are a helpful and concise cybersecurity assistant for the SBAJ portal. Provide accurate, safety-focused advice.' },
                { role: 'user', content: message }
            ],
            temperature: 0.7
        })
    });

    if (!response.ok) throw new Error(`OpenAI Chat Error: ${response.statusText}`);
    const data = await response.json();
    return data.choices[0].message.content;
}

async function chatWithGoogle(message: string, apiKey: string, model: string): Promise<string> {
    const actualModel = resolveModel('google', model);
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${actualModel || 'gemini-1.5-pro'}:generateContent?key=${apiKey}`;

    // Gemini 1.5 doesn't strictly separate system/user in the same way for simple calls, 
    // but we can prepend instructions.
    const fullPrompt = `You are a helpful and concise cybersecurity assistant for the SBAJ portal.\n\nUser: ${message}`;

    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            contents: [{ parts: [{ text: fullPrompt }] }]
        })
    });

    if (!response.ok) throw new Error(`Google Chat Error: ${response.statusText}`);
    const data = await response.json();
    return data.candidates?.[0]?.content?.parts?.[0]?.text || "I couldn't generate a response.";
}

async function chatWithAnthropic(message: string, apiKey: string, model: string): Promise<string> {
    const actualModel = resolveModel('anthropic', model);
    const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey,
            'anthropic-version': '2023-06-01',
            'dangerously-allow-browser': 'true'
        },
        body: JSON.stringify({
            model: actualModel || 'claude-3-opus-20240229',
            max_tokens: 1000,
            system: "You are a helpful and concise cybersecurity assistant for the SBAJ portal.",
            messages: [
                { role: 'user', content: message }
            ]
        })
    });

    if (!response.ok) throw new Error(`Anthropic Chat Error: ${response.statusText}`);
    const data = await response.json();
    return data.content?.[0]?.text || "I couldn't generate a response.";
}
